import { useMediaLightbox } from '#pplx/frontend/apps/ask/asset-utils/useMediaLightbox.js';
import { canSideViewerRenderLocalFile } from '#pplx/frontend/apps/ask/files/file-utils/fileUtils.js';
import { useFileViewerContext } from '#pplx/frontend/apps/ask/shared-ui-components/fileViewerTypes.js';
import { getMimeTypeFromUrl } from '#pplx/frontend/common/files/urls.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { useCallback, useEffect, useRef } from 'react';
const LOCAL_PREVIEW_WINDOW_CLOSED_POLL_INTERVAL_MS = 1000;
const LOCAL_ATTACHMENT_DOWNLOAD_MIME_TYPES = new Set([
    'application/ecmascript',
    'application/javascript',
    'application/xhtml+xml',
    'application/xml',
    'text/ecmascript',
    'text/html',
    'text/javascript',
    'text/xml',
]);
function getTargetMimeType(target) {
    switch (target.kind) {
        case 'local':
            return target.file.type || getMimeTypeFromUrl(target.file.name);
        case 'remote':
            return target.mimeType || getMimeTypeFromUrl(target.fileName);
        default:
            return assertUnreachableValue(target);
    }
}
function getPreviewPresentation(target, canOpenFiles) {
    const mimeType = getTargetMimeType(target);
    // Remote targets are raw upload URLs that the viewer cannot fetch without a thread to sign them.
    if (canOpenFiles &&
        target.kind === 'local' &&
        canSideViewerRenderLocalFile(target.file.name, mimeType)) {
        return 'fileViewer';
    }
    if (mimeType.startsWith('image/'))
        return 'imageLightbox';
    if (mimeType.startsWith('video/'))
        return 'videoLightbox';
    if (target.kind === 'local' && LOCAL_ATTACHMENT_DOWNLOAD_MIME_TYPES.has(mimeType)) {
        return 'download';
    }
    return 'newTab';
}
function downloadLocalAttachment(file) {
    const objectUrl = URL.createObjectURL(file);
    const anchor = document.createElement('a');
    try {
        anchor.href = objectUrl;
        anchor.download = file.name;
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
    }
    finally {
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
    }
}
function openAttachmentInNewTab(target, reservedWindow) {
    switch (target.kind) {
        case 'local':
            break;
        case 'remote':
            if (reservedWindow) {
                if (!reservedWindow.closed)
                    reservedWindow.location.replace(target.url);
            }
            else {
                window.open(target.url, '_blank', 'noopener,noreferrer');
            }
            return;
        default:
            return assertUnreachableValue(target);
    }
    const objectUrl = URL.createObjectURL(target.file);
    const previewWindow = window.open('', '_blank');
    if (!previewWindow) {
        URL.revokeObjectURL(objectUrl);
        return;
    }
    try {
        // A window handle lets the object URL remain valid for reloads and downloads. Sever the opener
        // before loading user-controlled bytes, then release the URL only when that window closes.
        previewWindow.opener = null;
        previewWindow.location.replace(objectUrl);
    }
    catch {
        previewWindow.close();
        URL.revokeObjectURL(objectUrl);
        return;
    }
    const closedPoll = window.setInterval(() => {
        if (!previewWindow.closed)
            return;
        window.clearInterval(closedPoll);
        URL.revokeObjectURL(objectUrl);
    }, LOCAL_PREVIEW_WINDOW_CLOSED_POLL_INTERVAL_MS);
}
function getMediaSource(target) {
    switch (target.kind) {
        case 'local':
            return { fileName: target.file.name, url: URL.createObjectURL(target.file) };
        case 'remote':
            return { fileName: target.fileName, url: target.url };
        default:
            return assertUnreachableValue(target);
    }
}
export function useAttachmentPreviewController() {
    // Object URL for the local file in the lightbox, released when replaced or on unmount so the
    // image stays intact through the lightbox exit animation.
    const localMediaUrl = useRef(null);
    const releaseLocalMediaUrl = useCallback(() => {
        if (!localMediaUrl.current)
            return;
        URL.revokeObjectURL(localMediaUrl.current);
        localMediaUrl.current = null;
    }, []);
    const { open, lightboxElement, closeLightbox } = useMediaLightbox({
        reason: 'composer-attachment',
    });
    const closePreview = useCallback(() => {
        closeLightbox();
        releaseLocalMediaUrl();
    }, [closeLightbox, releaseLocalMediaUrl]);
    const { canOpenFiles: canOpenInSideViewer, openFile: openInSideViewer } = useFileViewerContext();
    // Resolvers can finish after unmount or a newer preview. Incrementing on each transition prevents
    // an older response from reopening a dismissed preview or replacing the current one.
    const previewGeneration = useRef(0);
    useEffect(() => () => {
        previewGeneration.current += 1;
        releaseLocalMediaUrl();
    }, [releaseLocalMediaUrl]);
    const openAttachmentPreview = useCallback((target) => {
        const generation = ++previewGeneration.current;
        const presentation = getPreviewPresentation(target, canOpenInSideViewer);
        const showMedia = (previewTarget, type) => {
            releaseLocalMediaUrl();
            const { fileName, url } = getMediaSource(previewTarget);
            if (previewTarget.kind === 'local')
                localMediaUrl.current = url;
            open({ type, url, title: fileName, filename: fileName, downloadFromSignedUrl: true });
        };
        const showPreview = (previewTarget, reservedWindow) => {
            switch (presentation) {
                case 'download':
                    closePreview();
                    if (previewTarget.kind === 'local')
                        downloadLocalAttachment(previewTarget.file);
                    break;
                case 'fileViewer': {
                    closePreview();
                    if (previewTarget.kind !== 'local')
                        break;
                    const url = URL.createObjectURL(previewTarget.file);
                    openInSideViewer({ name: previewTarget.file.name, url, downloadUrl: url }, { isBlobUrl: true });
                    break;
                }
                case 'imageLightbox':
                    showMedia(previewTarget, 'image');
                    break;
                case 'videoLightbox':
                    showMedia(previewTarget, 'video');
                    break;
                case 'newTab':
                    if (!reservedWindow)
                        closePreview();
                    openAttachmentInNewTab(previewTarget, reservedWindow);
                    break;
                default:
                    assertUnreachableValue(presentation);
            }
        };
        const isRemote = target.kind === 'remote';
        if (!isRemote) {
            showPreview(target);
            return presentation;
        }
        const { url } = target;
        if (typeof url === 'string') {
            showPreview({ ...target, url });
            return presentation;
        }
        closePreview();
        // Reserve document tabs during the user gesture, before asynchronous authorization.
        const reservedWindow = presentation === 'newTab' ? window.open('', '_blank') : undefined;
        if (presentation === 'newTab' && !reservedWindow)
            return presentation;
        if (reservedWindow)
            reservedWindow.opener = null;
        void Promise.resolve()
            .then(url)
            .then((resolvedUrl) => {
            if (!resolvedUrl) {
                reservedWindow?.close();
                return;
            }
            // Separate document tabs remain independent of the active media lightbox.
            if (!reservedWindow && generation !== previewGeneration.current)
                return;
            showPreview({ ...target, url: resolvedUrl }, reservedWindow ?? undefined);
        })
            .catch(() => reservedWindow?.close());
        return presentation;
    }, [canOpenInSideViewer, closePreview, open, openInSideViewer, releaseLocalMediaUrl]);
    return { openAttachmentPreview, previewOverlay: lightboxElement };
}
//# sourceMappingURL=useAttachmentPreviewController.js.map